/**
 * Coverage — the supply-side surface on the Client Success Coverage tab
 * (ADR-0101).
 *
 * Wire shape for GET /api/firm-success/coverage: a FLAT array of enriched
 * Coverage rows — one per (firm, state, case type) cell — and nothing else.
 * The web groups client-side (by state for the choropleth fills, by state ×
 * case type for the detail card) because grouping depends on the live
 * chip selection, which is client state.
 *
 * The worker composer joins the Coverage table with the firm roster and
 * DROPS rows it cannot place on the map — null state, null case type, or an
 * unmatched/empty firm link — logging the dropped count. So `state` and
 * `caseType` are non-nullable here by construction.
 *
 * A row is "active" for depth-shading purposes iff `rowPaused` is false AND
 * `firmRoutingStatus` is not "Paused". Paused rows still ride the wire — the
 * detail card shows them muted with a "paused" tag rather than hiding them —
 * so the map never lies about the roster.
 *
 * NOTE: `@command-center/adapter-airtable` exports its own `CoverageRow` (the
 * raw table projection). This is the enriched wire shape; the composer aliases
 * the adapter import to keep the two distinct.
 */

import { z } from "zod";

export const CoverageRowSchema = z.object({
  /** Coverage record id. */
  id: z.string(),
  /** Owning firm record id (join verified server-side). */
  firmId: z.string(),
  /** Firm display name. */
  firmName: z.string(),
  /** Firm-level routing status — "Active" | "Paused" | null. */
  firmRoutingStatus: z.string().nullable(),
  /** 2-letter USPS state code. Never null on the wire. */
  state: z.string(),
  /** Case-type label. Never null on the wire. */
  caseType: z.string(),
  /** Routing priority (lower wins), or null when unset. */
  priority: z.number().nullable(),
  /** Handling-firm fee share as a decimal (0.67 = 67%), or null. */
  feeHfPct: z.number().nullable(),
  /** Per-row pause flag. */
  rowPaused: z.boolean(),
});

export const CoverageResponseSchema = z.object({
  rows: z.array(CoverageRowSchema),
});

export type CoverageRow = z.infer<typeof CoverageRowSchema>;
export type CoverageResponse = z.infer<typeof CoverageResponseSchema>;

// ---------------------------------------------------------------------------
// Coverage writes (ADR-0101 follow-on — Ops MCP write-coverage close, T1 #2)
// ---------------------------------------------------------------------------
// CC Phase 1 shipped Coverage read-only (the choropleth above). These back the
// write surface the Ops MCP wraps — POST /api/firm-success/coverage
// (create_coverage_row) and PATCH /api/firm-success/coverage/:id
// (update_coverage_row). Coverage is firm-routing config (who-covers-what + the
// fee split) and was the last live write-perimeter breach (ADR-0105): 100%
// hand-edited in Airtable. The portal routing brain READS this table
// (coverageEligibleFirms); the writer lives here, beside the reader, because
// every other firm-config write already runs through Client Success.
//
// `feeHfPct` is a DECIMAL fraction (0.67 = 67%) — same units as the Airtable
// percent field and the CoverageRow wire shape above. `priority` is a positive
// integer (lower wins). `state` / `caseType` are validated against the live
// Coverage singleSelect options at the route and never written raw — typecast
// would mint a new option, a schema change outside the data perimeter.

export const CoverageCreateRequestSchema = z.object({
  /** Handling firm record id (Designation Handling | Both — gated at the route). */
  firmId: z.string().min(1),
  /** 2-letter state code; resolved to a live Coverage option at the route. */
  state: z.string().min(1),
  /** Case-type label; resolved to a live Coverage option at the route. */
  caseType: z.string().min(1),
  /** Routing priority for this (firm, state, case type) cell — lower wins. */
  priority: z.number().int().positive(),
  /** HF fee share as a decimal (0.67 = 67%). Optional — fillable later. */
  feeHfPct: z.number().min(0).max(1).nullable().optional(),
  /** Start the row paused (excluded from routing). Default false. */
  rowPaused: z.boolean().optional(),
  /** #1212: true proceeds past the MFA-H handshake (an unpaused row on a
   *  Handling/Both Firm with no MFA-H on file and no MFA-H exemption). */
  confirm: z.boolean().optional(),
});
export type CoverageCreateRequest = z.infer<typeof CoverageCreateRequestSchema>;

/**
 * Request body for PATCH /api/firm-success/coverage/:id — the mutable levers
 * of an existing row. At least one required (a no-field PATCH is a 400, not a
 * silent no-op). `feeHfPct: null` clears the fee; identity (firm / state /
 * case type) is not mutable — re-point a cell by creating a new row.
 */
export const CoverageUpdateRequestSchema = z
  .object({
    priority: z.number().int().positive().optional(),
    feeHfPct: z.number().min(0).max(1).nullable().optional(),
    rowPaused: z.boolean().optional(),
    /** #1212: true proceeds past the MFA-H handshake when unpausing a paused
     *  row. Not a lever: a PATCH carrying only `confirm` is still a 400. */
    confirm: z.boolean().optional(),
  })
  .refine(
    (d) =>
      d.priority !== undefined ||
      d.feeHfPct !== undefined ||
      d.rowPaused !== undefined,
    { message: "at least one of priority, feeHfPct, rowPaused is required" },
  );
export type CoverageUpdateRequest = z.infer<typeof CoverageUpdateRequestSchema>;

/**
 * Echo from create / update — the RAW Coverage row projection (the adapter's
 * `CoverageRow`, NOT the enriched `CoverageRowSchema` wire shape above: no
 * firmName / firmRoutingStatus join, and firmId / state / caseType are nullable
 * as the tolerant projection returns them). The Ops MCP relays this back to the
 * operator; `id` is the handle `update_coverage_row` targets.
 */
export const CoverageMutationResponseSchema = z.object({
  id: z.string(),
  firmId: z.string().nullable(),
  state: z.string().nullable(),
  caseType: z.string().nullable(),
  priority: z.number().nullable(),
  feeHfPct: z.number().nullable(),
  rowPaused: z.boolean(),
});
export type CoverageMutationResponse = z.infer<
  typeof CoverageMutationResponseSchema
>;
